import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Calendar, CalendarEvent } from '../api/types';
import MailShell, { NavButton, useMail } from '../components/MailShell';
import { Empty, button, card, input } from '../components/Section';
import { locale, useLang, useT } from '../i18n';
import { byDay, dayKey, fromDayKey, keyOf, monthGrid, nextDay, rangeOf, toInstant, toLocalFields } from '../calendar/month';
import { useToasts } from '../ui/toasts';
import { usePanes } from '../ui/viewport';

/*
    The calendar.

    The events live on the same DAV server as the address book and are the same ones a phone syncs;
    this screen is a view onto them, not a place they are kept. Two things follow from that and both
    are visible here: an event that repeats is not this app's to reschedule (the server holds the
    rule, and moving one occurrence would move them all), and an event in the domain's shared
    calendar belongs to everybody with an address in it.

    The month arithmetic — which day a Sunday evening belongs to, whether an all-day event touches
    the day after — lives in ../calendar/month.ts, where a test can ask it directly.
*/

interface Draft {
    id: string;
    title: string;
    allDay: boolean;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    location: string;
    note: string;
    calendar: string;
    shared: boolean;
    recurring: boolean;
}

const NOTHING: unique symbol = Symbol('nothing');
type Destination = CalendarEvent | { newOn: string } | typeof NOTHING;

function draftOf(event: CalendarEvent): Draft {
    const from = toLocalFields(event.start, Boolean(event.allDay));
    // An all-day event ends the day after its last one. The form shows the last day somebody would
    // say it runs to, because "ends on the 13th" for a one-day event on the 12th reads as a bug.
    const rawEnd = toLocalFields(event.end || event.start, Boolean(event.allDay));
    const endDate = event.allDay ? lastDayOf(event) : rawEnd.date;
    return {
        id: event.id,
        title: event.title,
        allDay: Boolean(event.allDay),
        startDate: from.date,
        startTime: from.time || '09:00',
        endDate: endDate || from.date,
        endTime: rawEnd.time || from.time || '10:00',
        location: event.location ?? '',
        note: event.note ?? '',
        calendar: event.calendar,
        shared: Boolean(event.shared),
        recurring: Boolean(event.recurring),
    };
}

function lastDayOf(event: CalendarEvent): string {
    const end = fromDayKey(event.end || event.start);
    end.setDate(end.getDate() - 1);
    const last = dayKey(end);
    return last < event.start ? event.start : last;
}

function blankOn(day: string, calendar: Calendar | undefined): Draft {
    return {
        id: '',
        title: '',
        allDay: false,
        startDate: day,
        startTime: '09:00',
        endDate: day,
        endTime: '10:00',
        location: '',
        note: '',
        calendar: calendar?.path ?? '',
        shared: Boolean(calendar?.shared),
        recurring: false,
    };
}

/** The domain a shared calendar belongs to, taken from its path: /ortak-<domain>/. */
function domainOf(path: string): string {
    return /^\/ortak-([^/]+)\//.exec(path)?.[1] ?? '';
}

export default function CalendarView() {
    return (
        <MailShell section="calendar">
            <CalendarPanel />
        </MailShell>
    );
}

function CalendarPanel() {
    const { client } = useMail();
    const { t, num } = useT();
    const lang = useLang((s) => s.lang);
    const { onePane } = usePanes();
    const show = useToasts((s) => s.show);

    const today = useMemo(() => new Date(), []);
    const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
    const [calendars, setCalendars] = useState<Calendar[]>([]);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [missing, setMissing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [chosen, setChosen] = useState<string | null>(null);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [pending, setPending] = useState<Destination | null>(null);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const pristine = useRef('');

    const grid = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth(), today), [cursor, today]);
    const range = useMemo(() => rangeOf(grid), [grid]);

    const load = useCallback(async () => {
        if (!client) return;
        setLoading(true);
        try {
            const month = await client.events(range.from, range.to);
            if (!month) {
                setMissing(true);
                return;
            }
            setCalendars(month.calendars);
            setEvents(month.events);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [client, range.from, range.to]);

    useEffect(() => {
        void load();
    }, [load]);

    const writable = useMemo(() => calendars.filter((c) => !c.shared), [calendars]);
    // Ortak takvimin ait olduğu alan adı; yoksa boş — açıklama da o zaman çizilmiyor.
    const ortakAlan = useMemo(() => {
        const ortak = calendars.find((c) => c.shared);
        return ortak ? domainOf(ortak.path) : '';
    }, [calendars]);
    const days = useMemo(() => byDay(events), [events]);
    const dirty = draft !== null && JSON.stringify(draft) !== pristine.current;

    const open = useCallback((where: Destination, first: Calendar | undefined) => {
        let next: Draft | null = null;
        if (where !== NOTHING) next = 'newOn' in where ? blankOn(where.newOn, first) : draftOf(where);
        setDraft(next);
        pristine.current = next ? JSON.stringify(next) : '';
        setSaved(false);
        setError(null);
    }, []);

    const go = (where: Destination) => {
        if (dirty) {
            setPending(where);
            return;
        }
        open(where, writable[0] ?? calendars[0]);
    };

    const monthName = useMemo(
        () => new Intl.DateTimeFormat(locale(lang), { month: 'long', year: 'numeric' }).format(cursor),
        [cursor, lang],
    );
    const weekdays = useMemo(() => {
        const format = new Intl.DateTimeFormat(locale(lang), { weekday: 'short' });
        // A known Monday, so the row of names starts where the grid does.
        return Array.from({ length: 7 }, (_, i) => format.format(new Date(2026, 0, 5 + i)));
    }, [lang]);
    const longDay = useMemo(
        () => new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' }),
        [lang],
    );
    const clock = useMemo(
        () => new Intl.DateTimeFormat(locale(lang), { hour: '2-digit', minute: '2-digit', hour12: false }),
        [lang],
    );

    const move = (by: number) => {
        setCursor((c) => new Date(c.getFullYear(), c.getMonth() + by, 1));
        if (!dirty) open(NOTHING, undefined);
    };

    const save = async (): Promise<boolean> => {
        if (!client || !draft || !draft.startDate) return false;
        setBusy(true);
        try {
            const payload = draft.allDay
                ? { start: draft.startDate, end: nextDay(draft.endDate || draft.startDate), allDay: true }
                : {
                      start: toInstant(draft.startDate, draft.startTime),
                      end: toInstant(draft.endDate || draft.startDate, draft.endTime),
                      allDay: false,
                  };
            const result = await client.saveEvent({
                ...(draft.id ? { id: draft.id } : {}),
                title: draft.title.trim(),
                location: draft.location.trim(),
                note: draft.note.trim(),
                calendar: draft.calendar,
                ...payload,
            });
            setSaved(true);
            setError(null);
            // Read the month back rather than patching the list: the server decides what an event
            // became — a repeat keeps its own times, and an edit can change which days it touches.
            await load();
            const next = draftOf(result);
            setDraft(next);
            pristine.current = JSON.stringify(next);
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    /*
        Deleting happens at once and leaves the way back on the shelf, like every destructive action
        here. What goes back is the file the server handed over as it deleted it — an event carries
        an alarm and a guest list that this screen never draws.
    */
    const remove = async () => {
        if (!client || !draft?.id) return;
        const id = draft.id;
        const name = draft.title;
        setBusy(true);
        try {
            const gone = await client.deleteEvent(id);
            open(NOTHING, undefined);
            await load();
            show({
                message: 'calendar.deleted',
                values: { name },
                undo: gone.event
                    ? () => {
                          void client
                              .restoreEvent(id, gone.event)
                              .then(load)
                              .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
                      }
                    : undefined,
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const field = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

    if (missing) return <Empty title={t('calendar.title')} hint={t('calendar.none')} />;

    // Narrow: one thing at a time. The grid, or the day — never both squeezed side by side.
    const solo = onePane && (draft !== null || chosen !== null);
    const dayEvents = chosen ? (days.get(chosen) ?? []) : [];

    const chip = (event: CalendarEvent, compact: boolean, onDay?: string) => (
        <button
            key={keyOf(event)}
            data-testid="calendar-event"
            data-hit={compact ? undefined : true}
            onClick={(e) => {
                /*
                    The cell underneath also listens, and its job is to select the day and put the
                    form away. Without this, opening an event closed it again in the same click —
                    which is what the browser test caught: the form flashed and vanished.
                */
                e.stopPropagation();
                // The day this chip was drawn on, not the day the event begins: an event running
                // from Thursday to Saturday, clicked on the Saturday, should open Saturday.
                setChosen(onDay ?? dayKey(event.allDay ? fromDayKey(event.start) : new Date(event.start)));
                go(event);
            }}
            title={event.title}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                width: '100%',
                padding: compact ? '1px 4px' : '7px 9px',
                textAlign: 'left',
                background: event.shared ? 'var(--chip-bg)' : 'var(--accent-bg)',
                border: 'none',
                borderRadius: compact ? 4 : 'var(--r-btn)',
                color: 'var(--text)',
                fontSize: compact ? 'var(--fs-2xs)' : 'var(--fs-sm)',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            }}
        >
            {!event.allDay ? (
                /*
                    The time is dimmer than the title but NOT the muted grey the rest of the app
                    uses: that grey is contrast-checked against the page background, and on a chip
                    tinted with the accent it measures 4.45 — just under the line, for exactly the
                    people the line exists for. It keeps the chip's own text colour instead.
                */
                <span
                    style={{
                        flex: '0 0 auto',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--fs-2xs)',
                        letterSpacing: '0.5px',
                    }}
                >
                    {clock.format(new Date(event.start))}
                </span>
            ) : null}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</span>
            {event.recurring ? <span className="kicker" style={{ flex: '0 0 auto' }}>↻</span> : null}
        </button>
    );

    /*
        Gün panelindeki olay bir ÇİP değil bir kart.

        Ay ızgarasındaki çip tek satır olmak zorunda — bir hücreye üç tanesi sığacak. Gün paneli
        380 piksel geniş ve orada aynı tek satırı çizmek, yeri boşa harcayıp yerin kendisini
        gizliyordu: saat, başlık ve KONUM aynı satırda kısaltılıyor, konum çoğu zaman hiç
        görünmüyordu.

        Sol şerit ve sağa yuvarlanan köşe: kart bir sütunun içinde duruyor ve sol kenarı o sütuna
        yaslı — iki yanı da yuvarlatmak onu havada bırakırdı.
    */
    const dayCard = (event: CalendarEvent) => (
        <button
            key={event.id}
            data-testid="calendar-day-event"
            data-hit
            onClick={() => open(event, calendars.find((c) => c.path === event.calendar))}
            style={{
                display: 'flex',
                alignItems: 'stretch',
                width: '100%',
                padding: 0,
                textAlign: 'left',
                background: event.shared ? 'var(--chip-bg)' : 'var(--accent-bg)',
                border: 'none',
                borderRadius: '0 12px 12px 0',
                overflow: 'hidden',
            }}
        >
            <span
                aria-hidden="true"
                style={{ width: 3, flex: '0 0 auto', background: event.shared ? 'var(--text-mute)' : 'var(--accent)' }}
            />
            <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2, padding: '9px 11px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>
                        {event.allDay
                            ? t('calendar.allDay')
                            : `${clock.format(new Date(event.start))} – ${clock.format(new Date(event.end))}`}
                    </span>
                    {event.shared ? (
                        <span
                            data-testid="calendar-shared-badge"
                            style={{
                                fontSize: 'var(--fs-2xs)',
                                fontWeight: 600,
                                letterSpacing: '0.4px',
                                textTransform: 'uppercase',
                                padding: '0 5px',
                                borderRadius: 'var(--r-badge)',
                                background: 'var(--surface)',
                                color: 'var(--text-dim)',
                                border: '1px solid var(--border2)',
                            }}
                        >
                            {t('calendar.sharedBadge')}
                        </span>
                    ) : null}
                    {event.recurring ? <span aria-hidden="true" style={{ color: 'var(--text-mute)' }}>↻</span> : null}
                </span>
                <span style={{ fontSize: 'var(--fs-subject)', fontWeight: 600, overflowWrap: 'anywhere' }}>{event.title}</span>
                {event.location ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }} aria-hidden="true">
                            <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" />
                            <circle cx="12" cy="10" r="2.5" />
                        </svg>
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.location}</span>
                    </span>
                ) : null}
            </span>
        </button>
    );

    return (
        <div style={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0 }}>
            {solo && (draft !== null || chosen !== null) ? null : (
                <section style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, background: 'var(--bg)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 8px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                        <NavButton />
                        <button data-testid="calendar-prev" data-hit onClick={() => move(-1)} aria-label={t('calendar.previous')} style={{ ...button(), width: 30, padding: 0 }}>
                            ‹
                        </button>
                        <button data-testid="calendar-next" data-hit onClick={() => move(1)} aria-label={t('calendar.next')} style={{ ...button(), width: 30, padding: 0 }}>
                            ›
                        </button>
                        <div data-testid="calendar-month" style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>{monthName}</div>
                        {loading ? (
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>{t('app.loading')}</span>
                        ) : null}
                        {/*
                            İki renkli açıklama. Çok kiracılı bir takvimde "bu benim mi firmanın
                            mı" sorusu her olayda soruluyor ve cevabı bugün yalnız çipin zemin
                            renginde duruyordu — rengin ne anlama geldiğini söyleyen bir şey yoktu.

                            Ortak takvim yoksa açıklama da yok: tek renkli bir açıklama, olmayan
                            bir ayrımı anlatır.
                        */}
                        {ortakAlan ? (
                            <div data-testid="calendar-legend" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                {[
                                    { renk: 'var(--accent)', metin: t('calendar.mine') },
                                    { renk: 'var(--text-mute)', metin: t('calendar.theirs', { domain: ortakAlan }) },
                                ].map((g) => (
                                    <span key={g.metin} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-xs)', color: 'var(--text-dim)' }}>
                                        <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: g.renk, flex: '0 0 auto' }} />
                                        {g.metin}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        <div style={{ flex: 1 }} />
                        <button
                            data-testid="calendar-today"
                            data-hit
                            onClick={() => {
                                setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
                                setChosen(dayKey(today));
                            }}
                            style={button()}
                        >
                            {t('calendar.today')}
                        </button>
                        <button
                            data-testid="calendar-new"
                            data-hit
                            onClick={() => {
                                const day = chosen ?? dayKey(today);
                                setChosen(day);
                                go({ newOn: day });
                            }}
                            style={button(true)}
                        >
                            {t('calendar.newEvent')}
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', borderBottom: '1px solid var(--border)' }}>
                        {weekdays.map((name) => (
                            <div key={name} className="kicker" style={{ padding: '6px 8px', textAlign: 'center' }}>
                                {name}
                            </div>
                        ))}
                    </div>

                    {/* The grid takes the height it has: six equal rows, so a month with five weeks
                        and one with six are the same shape and nothing below them moves. */}
                    <div
                        data-testid="calendar-grid"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                            gridTemplateRows: 'repeat(6, minmax(0, 1fr))',
                            overflow: 'auto',
                        }}
                    >
                        {grid.map((day) => {
                            const list = days.get(day.key) ?? [];
                            const on = day.key === chosen;
                            return (
                                /*
                                    A cell, not a button: it holds buttons — one per event — and a
                                    button inside a button is neither valid nor operable. The click
                                    on the cell is a convenience for the mouse; the keyboard reaches
                                    the day through the number, which IS a button.
                                */
                                <div
                                    key={day.key}
                                    data-testid="calendar-day"
                                    data-day={day.key}
                                    onClick={() => {
                                        setChosen(day.key);
                                        if (draft && !dirty) open(NOTHING, undefined);
                                    }}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        gap: 2,
                                        minWidth: 0,
                                        minHeight: 68,
                                        padding: 4,
                                        textAlign: 'left',
                                        background: on
                                            ? 'var(--accent-bg)'
                                            : day.inMonth
                                              ? 'transparent'
                                              : 'var(--surface2)',
                                        borderRight: '1px solid var(--border)',
                                        borderBottom: '1px solid var(--border)',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <button
                                        data-testid="calendar-day-pick"
                                        data-hit
                                        aria-label={longDay.format(day.date)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setChosen(day.key);
                                            if (draft && !dirty) open(NOTHING, undefined);
                                        }}
                                        style={{
                                            alignSelf: 'flex-start',
                                            minWidth: 22,
                                            padding: '1px 5px',
                                            border: 'none',
                                            borderRadius: 'var(--r-pill)',
                                            fontSize: 'var(--fs-xs)',
                                            fontWeight: day.today ? 700 : 400,
                                            background: day.today ? 'var(--accent)' : 'transparent',
                                            color: day.today
                                                ? 'var(--on-accent)'
                                                : day.inMonth
                                                  ? 'var(--text-dim)'
                                                  : 'var(--text-mute)',
                                        }}
                                    >
                                        {num(day.date.getDate())}
                                    </button>
                                    {list.slice(0, 3).map((event) => chip(event, true, day.key))}
                                    {list.length > 3 ? (
                                        <span className="kicker" style={{ padding: '0 4px' }}>
                                            +{num(list.length - 3)}
                                        </span>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    {error && !draft ? (
                        <div style={{ padding: '8px 12px', fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{error}</div>
                    ) : null}
                </section>
            )}

            {chosen === null && draft === null ? null : (
                <aside
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: 'auto',
                        background: 'var(--surface)',
                        ...(onePane
                            ? { flex: 1 }
                            : { width: 380, flex: '0 0 auto', borderLeft: '1px solid var(--border)' }),
                    }}
                >
                    <div style={{ display: 'grid', gap: 12, padding: '14px 16px 40px' }}>
                        {solo ? (
                            <button
                                data-testid="calendar-back"
                                data-hit
                                onClick={() => {
                                    if (draft) go(NOTHING);
                                    else setChosen(null);
                                }}
                                style={{ ...button(), justifySelf: 'start' }}
                            >
                                ← {t('calendar.title')}
                            </button>
                        ) : null}

                        {draft === null ? (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <div data-testid="calendar-day-title" style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>
                                        {chosen ? longDay.format(fromDayKey(chosen)) : ''}
                                    </div>
                                    <div style={{ flex: 1 }} />
                                    <button data-testid="calendar-new-on-day" data-hit onClick={() => go({ newOn: chosen ?? dayKey(today) })} style={button(true)}>
                                        {t('calendar.newEvent')}
                                    </button>
                                </div>
                                {dayEvents.length ? (
                                    <div style={{ display: 'grid', gap: 6 }}>{dayEvents.map((event) => dayCard(event))}</div>
                                ) : (
                                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)' }}>{t('calendar.noEvents')}</div>
                                )}
                            </>
                        ) : (
                            <>
                                {pending !== null ? (
                                    <div
                                        data-testid="calendar-unsaved"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            flexWrap: 'wrap',
                                            padding: '9px 11px',
                                            background: 'var(--amber-bg)',
                                            border: '1px solid var(--amber)',
                                            borderRadius: 'var(--r-btn)',
                                        }}
                                    >
                                        <span style={{ flex: 1, minWidth: 150, fontSize: 'var(--fs-sm)', color: 'var(--amber)' }}>
                                            {t('calendar.unsavedQuestion')}
                                        </span>
                                        <button
                                            disabled={busy}
                                            onClick={() => {
                                                void save().then((ok) => {
                                                    if (!ok) return;
                                                    open(pending, writable[0] ?? calendars[0]);
                                                    setPending(null);
                                                });
                                            }}
                                            style={button(true)}
                                        >
                                            {t('app.save')}
                                        </button>
                                        <button
                                            onClick={() => {
                                                open(pending, writable[0] ?? calendars[0]);
                                                setPending(null);
                                            }}
                                            style={button()}
                                        >
                                            {t('compose.dontSave')}
                                        </button>
                                        <button onClick={() => setPending(null)} style={button()}>
                                            {t('calendar.keepEditing')}
                                        </button>
                                    </div>
                                ) : null}

                                {draft.shared ? (
                                    <div
                                        data-testid="calendar-shared-notice"
                                        style={{
                                            padding: '9px 11px',
                                            background: 'var(--chip-bg)',
                                            border: '1px solid var(--border2)',
                                            borderRadius: 'var(--r-btn)',
                                            fontSize: 'var(--fs-sm)',
                                            color: 'var(--text-dim)',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {t('calendar.sharedNotice', { domain: domainOf(draft.calendar) })}
                                    </div>
                                ) : null}

                                {draft.recurring ? (
                                    <div
                                        data-testid="calendar-recurring-notice"
                                        style={{
                                            padding: '9px 11px',
                                            background: 'var(--chip-bg)',
                                            border: '1px solid var(--border2)',
                                            borderRadius: 'var(--r-btn)',
                                            fontSize: 'var(--fs-sm)',
                                            color: 'var(--text-dim)',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {t('calendar.recurringNotice')}
                                    </div>
                                ) : null}

                                <div style={{ ...card, background: 'var(--surface2)' }}>
                                    <label style={{ display: 'grid', gap: 5 }}>
                                        <span className="kicker">// {t('calendar.name')}</span>
                                        <input
                                            data-testid="event-title"
                                            data-hit
                                            value={draft.title}
                                            onChange={(e) => field({ title: e.target.value })}
                                            style={{ ...input, flex: 'none', width: '100%' }}
                                        />
                                    </label>

                                    {/* The times of a repeat belong to the series, and the series is not
                                        this screen's to move — so they are absent here rather than shown
                                        as controls that would quietly change every other occurrence. */}
                                    {draft.recurring ? null : (
                                        <>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <input
                                                    data-testid="event-allday"
                                                    type="checkbox"
                                                    checked={draft.allDay}
                                                    onChange={(e) => field({ allDay: e.target.checked })}
                                                    style={{ accentColor: 'var(--accent)', width: 18, height: 18 }}
                                                />
                                                <span style={{ fontSize: 'var(--fs-sm)' }}>{t('calendar.allDay')}</span>
                                            </label>

                                            <div style={{ display: 'grid', gap: 5 }}>
                                                <span className="kicker">// {t('calendar.starts')}</span>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    <input
                                                        data-testid="event-start-date"
                                                        data-hit
                                                        type="date"
                                                        value={draft.startDate}
                                                        onChange={(e) => field({ startDate: e.target.value })}
                                                        style={input}
                                                    />
                                                    {draft.allDay ? null : (
                                                        <input
                                                            data-testid="event-start-time"
                                                            data-hit
                                                            type="time"
                                                            value={draft.startTime}
                                                            onChange={(e) => field({ startTime: e.target.value })}
                                                            style={input}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div style={{ display: 'grid', gap: 5 }}>
                                                <span className="kicker">// {t('calendar.ends')}</span>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    <input
                                                        data-testid="event-end-date"
                                                        data-hit
                                                        type="date"
                                                        value={draft.endDate}
                                                        onChange={(e) => field({ endDate: e.target.value })}
                                                        style={input}
                                                    />
                                                    {draft.allDay ? null : (
                                                        <input
                                                            data-testid="event-end-time"
                                                            data-hit
                                                            type="time"
                                                            value={draft.endTime}
                                                            onChange={(e) => field({ endTime: e.target.value })}
                                                            style={input}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <label style={{ display: 'grid', gap: 5 }}>
                                        <span className="kicker">// {t('calendar.where')}</span>
                                        <input
                                            data-testid="event-location"
                                            data-hit
                                            value={draft.location}
                                            onChange={(e) => field({ location: e.target.value })}
                                            style={{ ...input, flex: 'none', width: '100%' }}
                                        />
                                    </label>

                                    <label style={{ display: 'grid', gap: 5 }}>
                                        <span className="kicker">// {t('calendar.note')}</span>
                                        <textarea
                                            data-testid="event-note"
                                            data-hit
                                            value={draft.note}
                                            onChange={(e) => field({ note: e.target.value })}
                                            rows={3}
                                            style={{
                                                ...input,
                                                flex: 'none',
                                                width: '100%',
                                                height: 'auto',
                                                padding: '8px 10px',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                lineHeight: 1.5,
                                            }}
                                        />
                                    </label>

                                    {!draft.id && calendars.length > 1 ? (
                                        <label style={{ display: 'grid', gap: 5 }}>
                                            <span className="kicker">// {t('calendar.which')}</span>
                                            <select
                                                data-testid="event-calendar"
                                                data-hit
                                                value={draft.calendar}
                                                onChange={(e) => {
                                                    const picked = calendars.find((c) => c.path === e.target.value);
                                                    field({ calendar: e.target.value, shared: Boolean(picked?.shared) });
                                                }}
                                                style={{ ...input, flex: 'none', width: '100%' }}
                                            >
                                                {calendars.map((c) => (
                                                    <option key={c.path} value={c.path}>
                                                        {c.shared ? `${c.name} (${t('calendar.shared')})` : c.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    ) : null}

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <button
                                            data-testid="event-save"
                                            data-hit
                                            disabled={busy || !dirty || !draft.startDate}
                                            onClick={() => void save()}
                                            style={{ ...button(true), opacity: !dirty || !draft.startDate ? 0.5 : 1 }}
                                        >
                                            {t('app.save')}
                                        </button>
                                        {draft.id ? (
                                            <button
                                                data-testid="event-delete"
                                                data-hit
                                                disabled={busy}
                                                onClick={() => void remove()}
                                                style={{ ...button(), color: 'var(--red)', borderColor: 'var(--red)' }}
                                            >
                                                {t('calendar.delete')}
                                            </button>
                                        ) : null}
                                        {saved && !dirty ? <span className="kicker">// {t('calendar.saved')}</span> : null}
                                    </div>

                                    {error ? <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--red)' }}>{error}</div> : null}
                                </div>
                            </>
                        )}
                    </div>
                </aside>
            )}
        </div>
    );
}
